import { useEffect } from 'react';

const CONSENT_KEY = 'showgroom_cookie_consent';

export type ConsentState = 'granted' | 'denied';

export interface ConsentPreferences {
  analytics_storage: ConsentState;
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
}

/** Read saved consent from localStorage, or null if not yet decided. */
export function getSavedConsent(): ConsentPreferences | null {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
}

/** Save consent to localStorage. */
export function saveConsent(prefs: ConsentPreferences) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
}

/** Push a gtag consent update to the dataLayer. */
export function updateGtagConsent(prefs: ConsentPreferences) {
  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', prefs);
  }
}

interface GoogleAnalyticsProps {
  measurementId: string;
}

export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  useEffect(() => {
    if (!measurementId || measurementId.startsWith('G-XXXXXXXXXX')) {
      return;
    }

    const w = window as any;

    // Step 1: Initialize dataLayer and gtag function FIRST
    w.dataLayer = w.dataLayer || [];
    function gtag(...args: any[]) {
      w.dataLayer.push(args);
    }
    w.gtag = gtag;

    // Step 2: Set consent defaults BEFORE loading gtag.js
    const saved = getSavedConsent();
    gtag('consent', 'default', {
      ad_storage: saved?.ad_storage ?? 'denied',
      ad_user_data: saved?.ad_user_data ?? 'denied',
      ad_personalization: saved?.ad_personalization ?? 'denied',
      analytics_storage: saved?.analytics_storage ?? 'denied',
      wait_for_update: 500,
    });

    // Step 3: Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    document.head.appendChild(script);

    // Step 4: Configure GA4
    gtag('js', new Date());
    gtag('config', measurementId, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    });

    return () => {
      script.remove();
    };
  }, [measurementId]);

  return null;
}

interface GoogleSearchConsoleProps {
  verificationCode: string;
}

export function GoogleSearchConsole({ verificationCode }: GoogleSearchConsoleProps) {
  useEffect(() => {
    if (!verificationCode || verificationCode.startsWith('GSC_META_TAG')) {
      return;
    }

    const meta = document.createElement('meta');
    meta.name = 'google-site-verification';
    meta.content = verificationCode;
    document.head.appendChild(meta);

    return () => {
      meta.remove();
    };
  }, [verificationCode]);

  return null;
}

// Google Tag Manager
interface GoogleTagManagerProps {
  containerId: string;
}

export function GoogleTagManager({ containerId }: GoogleTagManagerProps) {
  useEffect(() => {
    if (!containerId || containerId.startsWith('GTM-XXXXX')) {
      return;
    }

    // GTM script
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(script);

    // GTM noscript
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      script.remove();
      noscript.remove();
    };
  }, [containerId]);

  return null;
}
